import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Check, Copy, ListChecks, RefreshCw, Sparkles, Target } from 'lucide-react';
import { useWorkspace, authHeaders } from '../../context/WorkspaceContext';
import { AI_CATEGORIES, AI_TOOLS, PLAN_LEVEL, PLANS } from '../../plans';
import { aiToolGuide } from '../../workspaceGuide';
import { Alert, Badge, Button, EmptyState, FormField, PageHeader, SectionHeader, Skeleton, UpgradeGate } from '../../components/ui';
import ToolWorkspace from '../../components/ui/ToolWorkspace';
import { useToast } from '../../components/ui/ToastProvider';
import WebsiteBuilder from '../WebsiteBuilder';
import { parseJSON } from './insights';
import './dashboard.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const BLANK = { business: '', industry: '', audience: '', offer: '', location: '', goal: '', tone: 'clear and confident', notes: '' };

const draftText = draft => [
  draft.title, draft.headline, draft.summary,
  ...(draft.sections || []).flatMap(section => [section.heading, section.body]),
  ...(draft.actions || []),
].filter(Boolean).join('\n\n');

function BriefField({ field, value, onChange }) {
  const id = `brief-${field.name}`;
  const shared = { id, name: field.name, value, onChange, required: field.required };
  return (
    <FormField label={field.label} required={field.required} help={field.help} wide={field.type === 'textarea'} id={id}>
      {field.type === 'select'
        ? <select className="dt-select" {...shared}>{field.options.map(option => <option key={option}>{option}</option>)}</select>
        : field.type === 'textarea'
          ? <textarea className="dt-textarea" placeholder={field.placeholder} {...shared} />
          : <input className="dt-input" placeholder={field.placeholder} {...shared} />}
    </FormField>
  );
}

export default function AIToolPage() {
  const { toolId } = useParams();
  const [searchParams] = useSearchParams();
  const { workspace, loading, reload } = useWorkspace();
  const { notify } = useToast();

  const tool = AI_TOOLS.find(item => item.id === toolId) || AI_TOOLS[0];
  const guide = aiToolGuide(tool.id);
  const category = AI_CATEGORIES.find(item => item.id === tool.category);
  const unlocked = (PLAN_LEVEL[workspace.plan] || 1) >= PLAN_LEVEL[tool.minimum];

  const prefill = useMemo(() => {
    const values = {};
    Object.keys(BLANK).forEach(key => { const value = searchParams.get(key); if (value) values[key] = value; });
    return values;
  }, [searchParams]);

  const [brief, setBrief] = useState({ ...BLANK, ...prefill });
  const [generation, setGeneration] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const profile = useRef(workspace.optimization);
  profile.current = workspace.optimization;

  useEffect(() => {
    setBrief({
      ...BLANK,
      business: prefill.business || profile.current?.business || '',
      location: prefill.location || profile.current?.location || '',
      ...prefill,
    });
  }, [tool.id, prefill]);

  useEffect(() => {
    setGeneration((workspace.generations || []).find(item => item.tool_key === tool.id) || null);
  }, [tool.id, workspace.generations]);

  const draft = generation ? parseJSON(generation.output_data) : null;
  const history = (workspace.generations || []).filter(item => item.tool_key === tool.id).slice(0, 6);

  const change = event => setBrief(current => ({ ...current, [event.target.name]: event.target.value }));

  const generate = async event => {
    event.preventDefault();
    setBusy(true); setError(''); setCopied(false);
    try {
      const response = await fetch(`${API}/tools/ai-generate`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ toolKey: tool.id, ...brief }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not create this draft.');
      setGeneration(data.generation);
      await reload();
      notify(`${tool.name} draft created.`);
    } catch (requestError) {
      setError(requestError.message);
      notify(requestError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(draftText(draft));
    setCopied(true);
    notify('Draft copied to your clipboard.');
    setTimeout(() => setCopied(false), 1600);
  };

  const breadcrumb = [{ label: 'AI Studio', to: '/dashboard/ai-studio' }, { label: tool.name }];

  if (loading) return <div className="dt-stack-4"><Skeleton height={80} /><Skeleton height={420} /></div>;

  if (!unlocked) {
    return (
      <div className="dt-stack-5">
        <PageHeader title={tool.name} description={tool.description} breadcrumb={breadcrumb} />
        <UpgradeGate
          plan={tool.minimum}
          title={`Unlock ${tool.name}`}
          description={`${guide.useCase} Included with ${PLANS[tool.minimum].name} and every plan above it.`}
        />
      </div>
    );
  }

  const isWebsite = tool.id === 'website_builder' && draft;

  const configPanel = (
    <form className="dt-stack-4" onSubmit={generate}>
      <div className="dt-alert is-info"><Target size={15} aria-hidden="true" /><span>{guide.useCase}</span></div>
      <div className="dt-field-grid">
        {guide.fields.filter(field => field.type !== 'textarea').map(field => (
          <BriefField key={field.name} field={field} value={brief[field.name] ?? ''} onChange={change} />
        ))}
      </div>
      {guide.fields.filter(field => field.type === 'textarea').map(field => (
        <BriefField key={field.name} field={field} value={brief[field.name] ?? ''} onChange={change} />
      ))}
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="dt-page-header-actions">
        <Button type="submit" loading={busy}>{busy ? 'Generating...' : draft ? 'Regenerate draft' : 'Generate draft'}{!busy && <Sparkles size={15} />}</Button>
      </div>
      <p className="dt-support">{workspace.limits?.aiGenerations ?? 25} generations included each month on your plan.</p>
    </form>
  );

  const outputPanel = draft
    ? (
      <div className="tool-draft">
        <header>
          <span className="dt-label">{generation.provider === 'openai' ? 'OpenAI provider' : 'Built-in draft engine'}</span>
          <h3>{draft.headline || draft.title}</h3>
          {draft.summary && <p className="dt-body">{draft.summary}</p>}
        </header>
        {draft.sections?.map(section => (
          <section key={section.heading}><h4>{section.heading}</h4><p>{section.body}</p></section>
        ))}
        {draft.actions?.length > 0 && (
          <ul className="tool-actions-list">{draft.actions.map(action => <li key={action}><Check size={13} aria-hidden="true" />{action}</li>)}</ul>
        )}
      </div>
    )
    : (
      <EmptyState
        icon={ListChecks}
        title={`What ${tool.name} returns`}
        description="Fill in the details on the left. Every draft is saved to this account so you can return to it later."
        actions={<ul className="tool-expected">{guide.outputs.map(output => <li key={output}><Check size={13} aria-hidden="true" />{output}</li>)}</ul>}
      />
    );

  return (
    <div className="dt-stack-5">
      <PageHeader
        title={tool.name}
        description={tool.description}
        breadcrumb={breadcrumb}
        actions={<Badge tone="neutral">{category?.label}</Badge>}
      />

      {isWebsite ? (
        <>
          <ToolWorkspace full output={<WebsiteBuilder key={generation.id} draft={draft} generation={generation} reload={reload} onGenerationChange={setGeneration} />} />
          <section className="dt-card dt-stack-4" aria-label="Website details">
            <SectionHeader title="Regenerate from a new brief" description="Change the details below to produce a different starting draft." />
            {configPanel}
          </section>
        </>
      ) : (
        <ToolWorkspace
          configTitle="Details"
          configDescription="Only the information this tool needs."
          config={configPanel}
          outputTitle="Draft"
          outputActions={draft && (
            <>
              <Button variant="secondary" size="sm" onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? 'Copied' : 'Copy'}</Button>
              <Button variant="secondary" size="sm" onClick={generate} loading={busy}><RefreshCw size={14} />Regenerate</Button>
            </>
          )}
          output={outputPanel}
        />
      )}

      {history.length > 1 && (
        <section className="dt-stack-3" aria-label="Draft history">
          <SectionHeader title="Draft history" description={`Previous ${tool.name} drafts saved to this account.`} />
          <div className="tool-history">
            {history.map(item => (
              <button type="button" key={item.id} onClick={() => setGeneration(item)}>
                <strong>{new Date(item.created_at).toLocaleDateString()}</strong>
                <small>{item.provider === 'openai' ? 'OpenAI' : 'Built-in engine'}</small>
              </button>
            ))}
          </div>
        </section>
      )}

      <p className="dt-support"><Link className="dt-btn is-link" to="/dashboard/ai-studio">← Back to AI Studio</Link></p>
    </div>
  );
}
